import { isProd } from './is-env'

/**
 * Marks an error class whose instances an error boundary renders as an expected state (for example "Page
 * unavailable" after a workspace closed), not a failure. React 19's default `onCaughtError` console.errors
 * every error a boundary catches; roots pass reportCaughtError instead (@w/utils-fe mountApp, the
 * @w/react-testing test root), which skips marked errors and reports the rest as React's default does.
 *
 * ```ts
 * class WorkspaceClosedError extends Error { readonly [expectedRenderError] = true }
 * ```
 */
export const expectedRenderError: unique symbol = Symbol.for('@w/expected-render-error')

export function isExpectedRenderError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && Reflect.get(error, expectedRenderError) === true
}

/**
 * createRoot `onCaughtError`: skips expected errors and logs the rest as React's default does. Production:
 * `console.error(error)` (react-dom-client.production.js defaultOnCaughtError). Development: the error, the
 * component that threw and the boundary that caught it (react-dom-client.development.js defaultOnCaughtError).
 */
export function reportCaughtError(error: unknown, errorInfo: { componentStack?: string; errorBoundary?: unknown }, production = isProd): void {
  if (isExpectedRenderError(error)) return
  if (production) {
    console.error(error)
    return
  }
  const component = /^\s*at (\S+)/.exec(errorInfo.componentStack ?? '')?.[1]
  const boundary = errorInfo.errorBoundary?.constructor.name
  // React prints "Anonymous" for a boundary without a name (errorBoundaryName || "Anonymous").
  const boundaryName = boundary === undefined || boundary === '' ? 'Anonymous' : boundary
  console.error('%o\n\n%s\n\n%s\n', error,
    component ? `The above error occurred in the <${component}> component.` : 'The above error occurred in one of your React components.',
    `React will try to recreate this component tree from scratch using the error boundary you provided, ${boundaryName}.`)
}
